/**
 * Submission Link — hook ↔ edge function contract helpers.
 *
 * Pure JS/TS functions with no React Native, Supabase client, or Deno
 * dependencies. Lives in its own file so the contract tests can import
 * it without dragging in expo-secure-store (ESM, breaks Jest).
 *
 * Phase 116.1-01: existed before as inline logic inside
 * `submissions.ts` that silently sent snake_case keys to the edge
 * function (Zod stripped them). Extraction + camelCase enforcement
 * fixes the "status not retained" bug.
 *
 * The edge function's Zod schemas (`createSubmissionLinkSchema`,
 * `updateSubmissionLinkSchema` in `supabase/functions/_shared/validation.ts`)
 * are the source of truth for the request shape. These builders mirror
 * those schemas. `.strict()` mode on the schemas (added in Task 3)
 * makes any future drift between the two surfaces fail loudly at the
 * edge function — instead of being silently accepted.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A submission link row as stored in `submission_links`.
 * Mirrors the public columns plus computed fields the edge function attaches.
 */
export interface SubmissionLink {
  id: string;
  investor_user_id: string;
  slug: string;
  is_active: boolean;
  thesis_message: string | null;
  max_storage_mb: number;
  current_storage_bytes: number;
  created_at: string;
  updated_at: string;
  /** Computed by API */
  submission_count?: number;
  /** Full public URL */
  url?: string;
}

export interface CreateLinkParams {
  slug: string;
  thesisMessage?: string;
  isActive?: boolean;
}

export interface UpdateLinkParams {
  slug?: string;
  thesisMessage?: string;
  isActive?: boolean;
}

/** Edge function POST response envelope. */
export interface CreateLinkEnvelope {
  success: boolean;
  link?: SubmissionLink;
}

/** Edge function GET response envelope. */
export interface ListLinksEnvelope {
  success: boolean;
  links?: SubmissionLink[];
}

// ---------------------------------------------------------------------------
// Request body builders
// ---------------------------------------------------------------------------

/**
 * Build the camelCase request body for POST `/submission-link`.
 * Matches `createSubmissionLinkSchema` in the edge function's
 * shared validation module.
 */
export function buildCreateLinkBody(params: CreateLinkParams): {
  slug: string;
  thesisMessage?: string;
  isActive: boolean;
} {
  return {
    slug: params.slug,
    ...(params.thesisMessage !== undefined ? { thesisMessage: params.thesisMessage } : {}),
    isActive: params.isActive ?? true,
  };
}

/**
 * Build the camelCase partial-update request body for PATCH `/submission-link`.
 * Matches `updateSubmissionLinkSchema` — every field optional, fields not
 * supplied are omitted (not set to undefined) so the edge function only
 * touches what the caller actually intends to change.
 */
export function buildUpdateLinkBody(params: UpdateLinkParams): {
  slug?: string;
  thesisMessage?: string;
  isActive?: boolean;
} {
  const body: { slug?: string; thesisMessage?: string; isActive?: boolean } = {};
  if (params.slug !== undefined) body.slug = params.slug;
  if (params.thesisMessage !== undefined) body.thesisMessage = params.thesisMessage;
  if (params.isActive !== undefined) body.isActive = params.isActive;
  return body;
}

// ---------------------------------------------------------------------------
// Response envelope unwrappers
// ---------------------------------------------------------------------------

/** Extract the `link` from a POST response. Returns null if missing or malformed. */
export function unwrapCreateResponse(envelope: unknown): SubmissionLink | null {
  if (!envelope || typeof envelope !== 'object') return null;
  const e = envelope as CreateLinkEnvelope;
  return e.link ?? null;
}

/** Extract the `links` array from a GET response. Returns empty array if missing or malformed. */
export function unwrapListResponse(envelope: unknown): SubmissionLink[] {
  if (!envelope || typeof envelope !== 'object') return [];
  const e = envelope as ListLinksEnvelope;
  return e.links ?? [];
}
